import { computed, ref, watch, type ComputedRef, type Ref } from 'vue';
import { getAvailableLlmProviders, LlmProvider, type LlmProviderSpec } from '@/api';
import { useOrganizationStore } from '@/stores/organizationStore';
import { isEnumValue } from '@/utils/enumUtils';
import { logger } from '@/utils/logger';
import { useGaiaToast } from '@/composables/useGaiaToast';

type ChatModelOption = { provider: LlmProvider; model: string } & {
  max_context_window_token_count: number;
  max_response_token_count: number;
};

// `| undefined` reflects reality: lookups miss for models without a configured
// display name, and noUncheckedIndexedAccess is off.
const modelDisplayNameByKey = ref<Record<string, string | undefined>>({});
const providerDisplayNameByKey = ref<Record<string, string | undefined>>({});
const providerDisplayNameByProviderModelKey = ref<Record<string, string | undefined>>({});

/**
 * Shared across every caller: the model list is the same org-wide data, and the
 * selection has to be, because the component that *picks* the model
 * (GaiaChatSettings) is not the one that *sends* it (GaiaChat). Per-instance
 * refs would leave the sender reading a stale localStorage snapshot.
 *
 * The selection is the model the *next new* thread starts with. A thread is
 * pinned to its model when it is created (`llm_provider` / `llm_model` on the
 * thread), so an open thread shows and uses its own, not this.
 *
 * Only these four are shared. The popover's own state (`showModelMenu`,
 * `selectedModelProviderLabel`, …) stays per-instance below — sharing it would
 * open the settings panel in every mounted chat at once.
 */
const availableChatModels = ref<ChatModelOption[]>([]);
const selectedChatModelOverride = ref<{ provider: LlmProvider; model: string } | null>(null);
const modelsLoading = ref(false);
const modelsLoaded = ref(false);

const CHAT_MODEL_STORAGE_PREFIX = 'gaia:chat-model:';

function chatModelStorageKey(orgUid: string): string {
  return `${CHAT_MODEL_STORAGE_PREFIX}${orgUid}`;
}

function readStoredChatModel(
  orgUid: string | undefined
): { provider: LlmProvider; model: string } | null {
  if (!orgUid) return null;
  try {
    const raw = localStorage.getItem(chatModelStorageKey(orgUid));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as { provider?: unknown }).provider === 'string' &&
      typeof (parsed as { model?: unknown }).model === 'string'
    ) {
      return parsed as { provider: LlmProvider; model: string };
    }
  } catch {
    // Ignore malformed storage entries; fall back to the first available model.
  }
  return null;
}

function storeChatModel(orgUid: string | undefined, provider: LlmProvider, model: string): void {
  if (!orgUid) return;
  try {
    localStorage.setItem(chatModelStorageKey(orgUid), JSON.stringify({ provider, model }));
  } catch {
    // Storage may be unavailable (private mode / quota); selection still works for the session.
  }
}

function clearStoredChatModel(orgUid: string | undefined): void {
  if (!orgUid) return;
  try {
    localStorage.removeItem(chatModelStorageKey(orgUid));
  } catch {
    // Storage may be unavailable (private mode); the loaded list already wins for this session.
  }
}

/**
 * A retired model (gpt-4o-mini → gpt-5-mini) otherwise sits in localStorage forever,
 * and `currentChatModelSelection` hands it back during the window before this list
 * loads — long enough to send a dead model id.
 */
function dropStoredChatModelIfRetired(orgUid: string | undefined): void {
  const stored = readStoredChatModel(orgUid);
  if (!stored) return;
  const stillOffered = availableChatModels.value.some(
    m => m.provider === stored.provider && m.model === stored.model
  );
  if (!stillOffered) clearStoredChatModel(orgUid);
}

/**
 * A thread stays pinned to the model it was created with and the API can't move it,
 * so a thread on a model the backend no longer offers can only be read. False until
 * the list has loaded, so a slow or failed load never locks a thread.
 */
export function isRetiredChatModel(provider: LlmProvider, model: string): boolean {
  return (
    modelsLoaded.value &&
    !availableChatModels.value.some(m => m.provider === provider && m.model === model)
  );
}

function providerModelKey(provider: string, model: string): string {
  return `${provider}::${model}`;
}

export function formatModelLabel(model: string, provider: string): string {
  return modelDisplayNameByKey.value[providerModelKey(provider, model)] ?? model;
}

export function formatModelProviderLabel(provider: string, model: string): string {
  return (
    providerDisplayNameByProviderModelKey.value[providerModelKey(provider, model)] ??
    providerDisplayNameByKey.value[provider] ??
    ''
  );
}

export function getModelOptionKey(
  modelOption: Pick<ChatModelOption, 'provider' | 'model'>
): string {
  return `${modelOption.provider}::${modelOption.model}`;
}

export interface UseModelSelectionReturn {
  showModelMenu: Ref<boolean>;
  modelsLoading: Ref<boolean>;
  modelsLoaded: Ref<boolean>;
  modelMenuError: Ref<string | null>;
  selectingModelKey: Ref<string | null>;
  availableChatModels: Ref<ChatModelOption[]>;
  selectedModelProviderLabel: Ref<string | null>;
  currentChatModelSelection: ComputedRef<{ provider: LlmProvider; model: string } | null>;
  chatModelProviderGroups: ComputedRef<
    { provider: string; label: string; models: ChatModelOption[] }[]
  >;
  filteredChatModels: ComputedRef<ChatModelOption[]>;
  isCurrentChatModel: (modelOption: Pick<ChatModelOption, 'provider' | 'model'>) => boolean;
  selectModelProviderGroup: (provider: string) => void;
  ensureModelMenuData: (force?: boolean) => Promise<void>;
  handleSelectChatModel: (modelOption: ChatModelOption) => void;
}

export function useModelSelection(): UseModelSelectionReturn {
  const organizationStore = useOrganizationStore();
  const messageApi = useGaiaToast();

  const showModelMenu = ref(false);
  const modelMenuError = ref<string | null>(null);
  const selectingModelKey = ref<string | null>(null);
  const selectedModelProviderLabel = ref<string | null>(null);

  const currentChatModelSelection = computed(() => {
    if (selectedChatModelOverride.value) {
      return selectedChatModelOverride.value;
    }

    const stored = readStoredChatModel(organizationStore.activeOrganization?.uid);
    if (stored) {
      const models = availableChatModels.value;
      if (
        models.length === 0 ||
        models.some(m => m.provider === stored.provider && m.model === stored.model)
      ) {
        return stored;
      }
    }

    const first = availableChatModels.value.at(0);
    return first ? { provider: first.provider, model: first.model } : null;
  });

  const chatModelProviderGroups = computed(() => {
    const groups = new Map<
      string,
      { provider: string; label: string; models: ChatModelOption[] }
    >();

    for (const modelOption of availableChatModels.value) {
      const providerGroupLabel = formatModelProviderLabel(modelOption.provider, modelOption.model);
      const existing = groups.get(providerGroupLabel);
      if (existing) {
        existing.models.push(modelOption);
        continue;
      }
      groups.set(providerGroupLabel, {
        provider: providerGroupLabel,
        label: providerGroupLabel,
        models: [modelOption],
      });
    }

    return Array.from(groups.values());
  });

  const filteredChatModels = computed(() => {
    const provider = selectedModelProviderLabel.value;
    if (!provider) return availableChatModels.value;
    return availableChatModels.value.filter(
      modelOption => formatModelProviderLabel(modelOption.provider, modelOption.model) === provider
    );
  });

  function syncSelectedModelProvider(): void {
    const groups = chatModelProviderGroups.value;
    if (groups.length === 0) {
      selectedModelProviderLabel.value = null;
      return;
    }

    if (
      selectedModelProviderLabel.value &&
      groups.some(group => group.provider === selectedModelProviderLabel.value)
    ) {
      return;
    }

    const current = currentChatModelSelection.value;
    if (current) {
      const currentProviderLabel = formatModelProviderLabel(current.provider, current.model);
      if (groups.some(group => group.provider === currentProviderLabel)) {
        selectedModelProviderLabel.value = currentProviderLabel;
        return;
      }
    }

    selectedModelProviderLabel.value = groups[0].provider;
  }

  function selectModelProviderGroup(provider: string): void {
    selectedModelProviderLabel.value = provider;
  }

  function isCurrentChatModel(modelOption: Pick<ChatModelOption, 'provider' | 'model'>): boolean {
    const current = currentChatModelSelection.value;
    return (
      current != null &&
      current.provider === modelOption.provider &&
      current.model === modelOption.model
    );
  }

  function mapProviderSpecsToChatModels(
    providers: Record<string, LlmProviderSpec>
  ): ChatModelOption[] {
    const nextModelDisplayNameByKey: Record<string, string> = {};
    const nextProviderDisplayNameByKey: Record<string, string> = {};
    const nextProviderDisplayNameByProviderModelKey: Record<string, string> = {};
    const models: ChatModelOption[] = [];

    for (const [providerKey, providerSpec] of Object.entries(providers)) {
      if (!isEnumValue(LlmProvider, providerKey)) continue;
      for (const [modelName, modelSpec] of Object.entries(providerSpec.llm_models)) {
        const key = providerModelKey(providerKey, modelName);
        if (modelSpec.display_name_model) {
          nextModelDisplayNameByKey[key] = modelSpec.display_name_model;
        }
        if (modelSpec.display_name_provider) {
          nextProviderDisplayNameByProviderModelKey[key] = modelSpec.display_name_provider;
          if (!nextProviderDisplayNameByKey[providerKey]) {
            nextProviderDisplayNameByKey[providerKey] = modelSpec.display_name_provider;
          }
        }

        models.push({
          provider: providerKey,
          model: modelName,
          max_context_window_token_count: modelSpec.max_context_window_token_count,
          max_response_token_count: modelSpec.max_response_token_count,
        });
      }
    }

    modelDisplayNameByKey.value = nextModelDisplayNameByKey;
    providerDisplayNameByKey.value = nextProviderDisplayNameByKey;
    providerDisplayNameByProviderModelKey.value = nextProviderDisplayNameByProviderModelKey;

    return models;
  }

  async function ensureModelMenuData(force = false): Promise<void> {
    if (modelsLoading.value) return;
    if (!force && modelsLoaded.value) return;

    modelsLoading.value = true;
    modelMenuError.value = null;

    try {
      if (!organizationStore.activeOrganization) {
        await organizationStore.fetchActiveOrganization();
      }

      const response = await getAvailableLlmProviders();
      if (!response.data) {
        throw new Error('Failed to load chat models');
      }

      availableChatModels.value = mapProviderSpecsToChatModels(response.data);
      dropStoredChatModelIfRetired(organizationStore.activeOrganization?.uid);
      syncSelectedModelProvider();
      modelsLoaded.value = true;
    } catch (error) {
      logger.error('Failed to load chat models:', error);
      modelMenuError.value = error instanceof Error ? error.message : 'Failed to load chat models';
    } finally {
      modelsLoading.value = false;
    }
  }

  function handleSelectChatModel(modelOption: ChatModelOption): void {
    const modelKey = getModelOptionKey(modelOption);
    if (selectingModelKey.value || isCurrentChatModel(modelOption)) return;

    selectingModelKey.value = modelKey;
    modelMenuError.value = null;

    try {
      selectedChatModelOverride.value = {
        provider: modelOption.provider,
        model: modelOption.model,
      };
      storeChatModel(
        organizationStore.activeOrganization?.uid,
        modelOption.provider,
        modelOption.model
      );
      // No toast — the pill and the picker's check mark both already show it.
      showModelMenu.value = false;
    } catch (error) {
      logger.error('Failed to update chat model:', error);
      modelMenuError.value = error instanceof Error ? error.message : 'Failed to update chat model';
      messageApi.error('Failed to update chat model');
    } finally {
      selectingModelKey.value = null;
    }
  }

  watch(showModelMenu, isOpen => {
    if (isOpen) {
      syncSelectedModelProvider();
      void ensureModelMenuData();
    }
  });

  watch(
    () => organizationStore.activeOrganization?.uid,
    () => {
      modelDisplayNameByKey.value = {};
      providerDisplayNameByKey.value = {};
      providerDisplayNameByProviderModelKey.value = {};
      selectedChatModelOverride.value = null;
      modelsLoaded.value = false;
    }
  );

  void ensureModelMenuData();

  return {
    showModelMenu,
    modelsLoading,
    modelsLoaded,
    modelMenuError,
    selectingModelKey,
    availableChatModels,
    selectedModelProviderLabel,
    currentChatModelSelection,
    chatModelProviderGroups,
    filteredChatModels,
    isCurrentChatModel,
    selectModelProviderGroup,
    ensureModelMenuData,
    handleSelectChatModel,
  };
}
